import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ArrowLink from 'common/components/ArrowLink';

/**
 * Designed to be connected to react-router, and to work with the
 * historyEntries redux store key managed by `common/store/historyEntries`.
 *
 * Renders a left-facing arrow, wrapped in an <a>nchor tag.
 *
 * Clicking on the link calls history.goBack(), unless the last page the
 * user visited wasn't on our site, in which case it navigates to the given
 * fallbackPath.
 *
 * Note: fallbackPath is not a full URL or URI, but a react router path (it gets
 * appended to the basename given to BrowserRouter). So e.g. in event-nav, which
 * has the basename '/events/138751', rendering a <BackButton /> with no props,
 * the fallback is navigating to '/events/138751', and
 * <BackButton fallbackPath="/overview" /> would have a fallback of
 * '/events/138751/overview'.
 */
class BackButton extends Component {
    static propTypes = {
        href: PropTypes.string,
        historyEntries: PropTypes.object.isRequired,
        history: PropTypes.object.isRequired,
        onClickBack: PropTypes.func,
        fallbackPath: PropTypes.string,
        externalFallbackHref: PropTypes.string,
        isDark: PropTypes.bool,
        text: PropTypes.string,
    };

    static defaultProps = {
        href: '',
        fallbackPath: '',
        externalFallbackHref: null,
        isDark: false,
        text: null,
        onClickBack: null,
    };

    baseClassName = 'BackButton';

    /**
     * If there is something to go back to on our site, return the href
     * of that previous history entry.
     *
     * createHref, given the entire lastHistoryEntry object, will build the
     * full link that we came from, including query strings and hashes.
     *
     * @return {string|null}
     */
    getBackHref = () => {
        const { previous } = this.props.historyEntries;

        const lastHistoryEntry = previous[previous.length - 1];

        return lastHistoryEntry ? this.props.history.createHref(lastHistoryEntry) : null;
    };

    /**
     * Create a link from the fallback path. This createHref helper function
     * takes the pathname you give it and appends it to the basename specified
     * wherever the react-router BrowserRouter was created.
     *
     * @return {string}
     */
    getFallbackHref = () => this.props.history.createHref({ pathname: this.props.fallbackPath });

    handleClick = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();

        if (this.props.onClickBack) {
            this.props.onClickBack();
            return;
        }

        if (this.props.href) {
            this.props.history.replace(this.props.href);
            return;
        }

        if (!this.getBackHref()) {
            // Don't pass a full href to `history.replace` - it also expects
            // an internal react router path like '/content' or '/settings'
            this.props.history.replace(this.props.fallbackPath);
            return;
        }

        this.props.history.goBack();
    };

    render() {
        const { isDark, externalFallbackHref, text, href } = this.props;

        // Sometimes the fallback that you want, when there's no on-site place
        // to go back to, is outside of the react router app you're in. In that
        // case, specify the full href as the externalFallbackHref prop, and
        // render it as a regular link with no click handler.
        if (!this.getBackHref() && externalFallbackHref) {
            return <ArrowLink isDark={isDark} href={externalFallbackHref} text={text} />;
        }

        return (
            <ArrowLink
                isDark={isDark}
                href={href || this.getBackHref() || this.getFallbackHref()}
                onClick={this.handleClick}
                text={text}
            />
        );
    }
}

export default BackButton;
